import { LucideIcon } from 'lucide-react'
import { Video, Code, GraduationCap, Briefcase, Calendar } from 'lucide-react'

interface ProjectStory {
  intentie: {
    title: string
    description: string
  }
  proces: {
    title: string
    description: string
    tools?: string[]
  }
  resultaat: {
    title: string
    description: string
    metrics?: string[]
  }
}

export interface Project {
  id: string
  title: string
  subtitle: string
  category: 'video' | 'apps' | 'ux' | 'ai'
  icon: LucideIcon
  thumbnail: string
  heroImage: string
  videoUrl?: string
  gallery?: string[]
  tags: string[]
  story: ProjectStory
  storyEn: ProjectStory
  systemContext: {
    pillar: string
    connection: string
  }
}

export const projects: Project[] = [
  {
    id: 'meester-djay',
    title: 'Meester djay',
    subtitle: 'Adaptief Leerplatform voor het Basisonderwijs',
    category: 'apps',
    icon: GraduationCap,
    thumbnail:
      'https://cdn.magicpatterns.com/uploads/b9aVRivgiucaoKFBynE5JX/Gemini_Generated_Image_vntnstvntnstvntn.png',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/b9aVRivgiucaoKFBynE5JX/Gemini_Generated_Image_vntnstvntnstvntn.png',
    videoUrl:
      'https://cdn.magicpatterns.com/uploads/aVWxcpJoeE6tgynnyarTNa/Schoolapp_Promo_Video_For_Testers.mp4',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/6NWfy8mxRUCFE72yW12JzP/Scherm%C2%ADafbeelding_2026-04-18_om_18.32.24.png',
      'https://cdn.magicpatterns.com/uploads/dT8jt4MdNHQoxh1ZpM6HyP/Scherm%C2%ADafbeelding_2026-04-18_om_18.31.43.png',
    ],
    tags: ['React', 'TypeScript', 'DDD', 'Education', 'AI'],
    story: {
      intentie: {
        title: 'Adaptief leren, zonder omwegen',
        description:
          'Een oefenplatform voor leerlingen in groep 6-8 dat aansluit op wat een kind al beheerst. De uitdaging: didactische logica vertalen naar een interface waarmee leerlingen zelfstandig verder kunnen.',
      },
      proces: {
        title: 'Domain-Driven Design & AI-Assisted Engineering',
        description:
          'Gebouwd met een strikte DDD-architectuur (11 domeinen, 0 deep import violations). Ik implementeerde een Learning Dependency Graph (LDG) met 263 vraagfamilies en een 6-stage Governance Pipeline voor content validatie. Dit alles werd gerealiseerd met Claude AI als pair programmer.',
        tools: [
          'React',
          'TypeScript',
          'Tailwind',
          'Framer Motion',
          'Claude AI',
        ],
      },
      resultaat: {
        title: 'Een product dat mee kan groeien',
        description:
          'Een adaptief platform met 10 vakgebieden, 24 vraagtypes en aparte portalen voor leerling, ouder en docent. De engine kiest vervolgstappen op basis van beheersing en geeft feedback die past bij de context.',
        metrics: [
          '263 vraagfamilies',
          '16 niveaustappen',
          '24 vraagtypes',
        ],
      },
    },
    storyEn: {
      intentie: {
        title: 'Adaptive learning without detours',
        description:
          'A practice platform for pupils in grades 6-8 that responds to what a child already understands. The challenge was translating teaching logic into an interface that lets pupils continue independently.',
      },
      proces: {
        title: 'Domain-Driven Design & AI-Assisted Engineering',
        description:
          'Built on a strict DDD architecture with 11 domains and no deep-import violations. I implemented a Learning Dependency Graph with 263 question families and a six-stage content-validation pipeline, using Claude AI as a pair programmer.',
        tools: ['React', 'TypeScript', 'Tailwind', 'Framer Motion', 'Claude AI'],
      },
      resultaat: {
        title: 'A product built to grow',
        description:
          'An adaptive platform with 10 subject areas, 24 question types, and dedicated portals for pupils, parents, and teachers. The engine selects next steps based on mastery and gives feedback that fits the context.',
        metrics: ['263 question families', '16 difficulty tiers', '24 question types'],
      },
    },
    systemContext: {
      pillar: 'Full-Stack Ownership',
      connection:
        'Dit project demonstreert mijn capaciteit om complexe, data-gedreven applicaties van A tot Z te ontwerpen en bouwen met een AI-native workflow.',
    },
  },
  {
    id: 'ai-video',
    title: 'AI Video Generation',
    subtitle: 'Creative Workflows with Kling & Veo 3',
    category: 'video',
    icon: Video,
    thumbnail:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/stCMFT6LkkVykASQVNXRp6/aac4bfad-b894-401d-92ac-ad2a80272b62_%281%29.png',
    heroImage:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/stCMFT6LkkVykASQVNXRp6/aac4bfad-b894-401d-92ac-ad2a80272b62_%281%29.png',
    videoUrl:
      'https://drive.google.com/file/d/14ZHULrGyJQkKQKuUvvja7mO2PQ8pq4y4/preview',
    tags: ['AI Video', 'Kling', 'Veo 3', 'Prompt Engineering', 'Creative Workflows'],
    story: {
      intentie: {
        title: 'Van idee naar video die klopt',
        description:
          'Hoe vertaal je een creatief idee snel naar video met een eigen stijl, ritme en verhaal? Ik gebruik generatieve video als werkbare productietool: van character-driven content tot korte formats die je kunt testen, bijschaven en delen.',
      },
      proces: {
        title: 'Generative Video Workflow',
        description:
          'Ik ontwikkel systematische workflows met Kling, Veo 3 en Runway ML. Per concept leg ik stijl, characters, shots, camerabeweging en prompts vast, zodat iteraties gericht blijven en losse generaties uitgroeien tot consistente content. Zo ontstaat een snelle route van creatief concept naar werkend video-prototype.',
        tools: [
          'Kling AI',
          'Veo 3',
          'Runway ML',
          'Structured Prompts',
          'Character & Shot Systems',
        ],
      },
      resultaat: {
        title: 'Formats die je kunt herhalen',
        description:
          'Een AI-video workflow die ruimte laat om te spelen, maar genoeg houvast geeft om scenes en formats consistent te houden. Zo kan ik een visuele wereld snel testen zonder de creatieve regie uit handen te geven.',
        metrics: [
          'Kling + Veo 3 workflows',
          'Character consistency',
          'Snelle video-prototypes',
        ],
      },
    },
    storyEn: {
      intentie: {
        title: 'From idea to video that feels right',
        description:
          'How do you turn a creative idea into video with its own style, rhythm, and story? I use generative video as a practical production tool, from character-driven content to short formats you can test, refine, and share.',
      },
      proces: {
        title: 'Generative Video Workflow',
        description:
          'I build structured workflows with Kling, Veo 3, and Runway ML. For each concept, I define the style, characters, shots, camera movement, and prompts so iteration stays focused and separate generations become consistent content.',
        tools: ['Kling AI', 'Veo 3', 'Runway ML', 'Structured Prompts', 'Character & Shot Systems'],
      },
      resultaat: {
        title: 'Formats you can repeat',
        description:
          'An AI-video workflow that leaves room to play while providing enough structure to keep scenes and formats consistent. It lets me test a visual world quickly without giving up creative direction.',
        metrics: ['Kling + Veo 3 workflows', 'Character consistency', 'Rapid video prototypes'],
      },
    },
    systemContext: {
      pillar: 'Generative AI Video',
      connection:
        'Deze case laat zien hoe ik generatieve video inzet om creatieve ideeën om te zetten in testbare formats, consistente scenes en snelle prototypes.',
    },
  },
  {
    id: 'bulkr',
    title: 'BULKR App Design',
    subtitle: 'AI-Driven Shopping Experience',
    category: 'apps',
    icon: Code,
    thumbnail:
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop',
    heroImage:
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&h=800&fit=crop',
    tags: ['React', 'AI', 'UX', 'E-commerce'],
    story: {
      intentie: {
        title: 'Bulk bestellen zonder gedoe',
        description:
          'Hoe maak je grote bestellingen overzichtelijk? Gebruikers willen snel inkopen, maar bestaande flows zijn vaak omslachtig. Dit concept onderzoekt waar AI kan helpen met suggesties en voorspellingen.',
      },
      proces: {
        title: 'UX Research → Rapid Prototyping',
        description:
          'Eerst user research: wat frustreert gebruikers? Dan schetsen, wireframes, en snel prototypen met React. AI-integratie voor slimme suggesties op basis van koopgedrag. Iteratief testen en verfijnen.',
        tools: ['Figma', 'React', 'TypeScript', 'AI API'],
      },
      resultaat: {
        title: 'Een kortere route naar bestellen',
        description:
          'Je begint met typen, krijgt relevante productsuggesties en rondt je bestelling af in drie stappen. De flow is ontworpen rond snelheid en overzicht.',
        metrics: ['3 stappen naar checkout', 'Snellere flow', 'AI-suggesties'],
      },
    },
    storyEn: {
      intentie: {
        title: 'Bulk ordering without the friction',
        description:
          'How do you make large orders easier to manage? Users want to buy quickly, but existing flows are often cumbersome. This concept explores where AI can help with suggestions and predictions.',
      },
      proces: {
        title: 'UX Research to Rapid Prototyping',
        description:
          'Start with user research: what gets in the way? Then sketch, wireframe, and prototype quickly in React. AI supports useful suggestions based on buying behaviour, refined through iteration.',
        tools: ['Figma', 'React', 'TypeScript', 'AI API'],
      },
      resultaat: {
        title: 'A shorter route to ordering',
        description:
          'Start typing, get relevant product suggestions, and complete an order in three steps. The flow is designed around speed and clarity.',
        metrics: ['3 steps to checkout', 'Faster flow', 'AI suggestions'],
      },
    },
    systemContext: {
      pillar: 'VibeCoding',
      connection:
        'Dit project toont mijn VibeCoding aanpak: van UX thinking naar werkende app in dagen, niet maanden.',
    },
  },
  {
    id: 'jdg-advies',
    title: 'JDG Advies',
    subtitle: 'AI-Powered Website voor Financieel Adviseur',
    category: 'apps',
    icon: Briefcase,
    thumbnail:
      'https://cdn.magicpatterns.com/uploads/vFJXrhRW7uhYauVuoPpbgy/Scherm%C2%ADafbeelding_2026-04-18_om_20.33.23.png',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/vFJXrhRW7uhYauVuoPpbgy/Scherm%C2%ADafbeelding_2026-04-18_om_20.33.23.png',
    tags: ['Web Design', 'AI Generation', 'UX', 'Finance'],
    story: {
      intentie: {
        title: 'Persoonlijk en Vertrouwd',
        description:
          'Een toegankelijke, betrouwbare online aanwezigheid creëren voor een financieel adviseur, waarbij persoonlijk contact centraal staat.',
      },
      proces: {
        title: 'Snel ontwerpen, bewust kiezen',
        description:
          'AI ingezet om snel varianten in design en content te verkennen. Daarna terug naar de kern: een heldere route naar een kennismaking en navigatie die niet in de weg zit.',
      },
      resultaat: {
        title: 'Persoonlijk, helder en gericht',
        description:
          'Een rustige website die vertrouwen opbouwt en bezoekers zonder omwegen naar een kennismaking brengt. AI hielp het ontwerp sneller te verfijnen, niet om keuzes over te nemen.',
      },
    },
    storyEn: {
      intentie: {
        title: 'Personal and reassuring',
        description:
          'Create an approachable, credible online presence for a financial adviser, while keeping personal contact at the centre.',
      },
      proces: {
        title: 'Design quickly, choose deliberately',
        description:
          'AI helped explore design and content variations quickly. Then the work returned to the essentials: a clear route to an introductory call and navigation that stays out of the way.',
      },
      resultaat: {
        title: 'Personal, clear, and focused',
        description:
          'A calm website that builds trust and brings visitors to an introductory call without detours. AI helped refine the design faster, not make the decisions.',
      },
    },
    systemContext: {
      pillar: 'VibeCoding',
      connection:
        'Dit project laat zien hoe AI kan worden ingezet om snel professionele, conversiegerichte websites te realiseren.',
    },
  },
  {
    id: 'vvgn-platform',
    title: 'VVGN Platform',
    subtitle: 'Complex Planningssysteem Dashboard',
    category: 'apps',
    icon: Calendar,
    thumbnail:
      'https://cdn.magicpatterns.com/uploads/eZaAPHvS4TzHce8t3mDTdS/Scherm%C2%ADafbeelding_2026-04-18_om_20.33.12.png',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/eZaAPHvS4TzHce8t3mDTdS/Scherm%C2%ADafbeelding_2026-04-18_om_20.33.12.png',
    tags: ['Dashboard', 'React', 'UX', 'Planning'],
    story: {
      intentie: {
        title: 'Overzicht in Complexiteit',
        description:
          'Het overzichtelijk maken van complexe planningsdata voor instructeurs. Van onoverzichtelijke lijsten naar een intuïtief dashboard voor aanwezigheidsregistratie.',
      },
      proces: {
        title: 'Terug naar de Essentie',
        description:
          'Ruis weghalen en logica toevoegen. Complexe data-structuren vertaald naar heldere UI-componenten in een React-omgeving, met een focus op de primaire taak: registreren.',
      },
      resultaat: {
        title: 'Intuïtieve Workflow',
        description:
          'Een overzichtelijk dashboard waar gebruikers direct zien wat ze moeten doen. Minder kliks, minder fouten, en een systeem dat gewoon "fijn voelt".',
      },
    },
    storyEn: {
      intentie: {
        title: 'Clarity in complex planning',
        description:
          'Make complex planning data easier for instructors to use. Turn scattered lists into an intuitive dashboard for attendance registration.',
      },
      proces: {
        title: 'Back to the essentials',
        description:
          'Remove noise and add logic. Complex data structures became clear UI components in React, centred around the primary task: registering attendance.',
      },
      resultaat: {
        title: 'An intuitive workflow',
        description:
          'A clear dashboard where people immediately see what needs to happen. Fewer clicks, fewer mistakes, and a system that simply feels good to use.',
      },
    },
    systemContext: {
      pillar: 'UX & Engineering',
      connection:
        'Een perfect voorbeeld van mijn aanpak: complexe domeinen (planning) vertalen naar een logische, schaalbare interface.',
    },
  },
]
